import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  buildHeaderDataShell,
} from '../../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  CtrlFnPlan,
  EfileCtrlFnDispatcherService,
  EfileCtrlFnEvent,
} from '../scrubbing-actions/efile-ctrlfn-dispatcher.service';

@Component({
    selector: 'gtw-efile-elements-grid-tpl',
    templateUrl: './efile-elements-grid-tpl.component.html',
    styleUrls: ['./efile-elements-grid-tpl.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileElementsGridTplComponent implements OnInit, OnDestroy {
  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private isInitialized = false;
  baseURLs: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  rowDataKeys: string[] = [];

  // Confirmation-modal state owned by this host. Dispatcher returns the
  // configuration; the template renders `<gtw-confirmation-modal>` bound
  // to these fields (see companion .html file).
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm Action',
    message: 'Are you sure you want to proceed?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingPlan: CtrlFnPlan | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private ctrlFnDispatcher: EfileCtrlFnDispatcherService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.filterParams = normalizeFilterParams(activeScreen);
    if (!this.isInitialized) {
      this.isInitialized = true;
      this.rowDataKeys =
        this.rowData && typeof this.rowData === 'object' && !Array.isArray(this.rowData)
          ? Object.keys(this.rowData)
          : [];
    }
    this.cdr.markForCheck();
  }

  refresh(): void {
    this.requestGridUpdate.emit('refresh');
  }

  // ---------------------------------------------------------------------------
  // gizmoGrid `(ggCallBack)` bridge
  //
  // Hosts embedding a `<div gg-grid>` inside this component should bind
  //   (ggCallBack)="onGridCallback($event)"
  // so callbacks flow through the shared dispatcher. See
  // `EfileCtrlFnDispatcherService` for the list of supported callNames.
  // ---------------------------------------------------------------------------

  onGridCallback(event: EfileCtrlFnEvent | null | undefined): void {
    const plan = this.ctrlFnDispatcher.plan(event);
    if (!plan) return;

    if (!plan.confirmConfig) {
      this.executePlan(plan);
      return;
    }

    // Queue the action, surface the confirmation modal; `onConfirmModal`
    // picks it up from here.
    this.pendingPlan = plan;
    this.confirmModalConfig = { ...this.confirmModalConfig, ...plan.confirmConfig };
    this.showConfirmModal = true;
    this.cdr.markForCheck();
  }

  onConfirmModal(): void {
    const plan = this.pendingPlan;
    this.showConfirmModal = false;
    this.pendingPlan = null;
    this.cdr.markForCheck();
    if (plan) {
      this.executePlan(plan);
    }
  }

  onCancelModal(): void {
    this.showConfirmModal = false;
    this.pendingPlan = null;
    this.cdr.markForCheck();
  }

  private executePlan(plan: CtrlFnPlan): void {
    this.subs.add(
      plan.execute().subscribe((result) => {
        if (result === 'refresh') {
          this.refresh();
        }
      }),
    );
  }
}
